
import React, { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Eye, AlertTriangle, CheckCircle, MapPin, User, Clock, Binoculars, Trash2 } from 'lucide-react';
import { format } from 'date-fns';
import { AlertDetailDialog } from './AlertDetailDialog';
import { cn } from '@/lib/utils';
import { useUserRoles } from '@/hooks/useUserRoles';
import { useClientTeams } from '@/hooks/useClientTeams';
import { useSupabaseUser } from '@/hooks/use-supabase-user';
import { toast } from 'sonner';

interface PatrolObservation {
  id: string;
  title: string;
  description: string;
  severity: 'low' | 'medium' | 'high';
  status: 'pending' | 'resolved';
  timestamp: string;
  guard_name: string;
  team_id: string;
}

export const PatrolObservationsList = () => {
  const [selectedObservation, setSelectedObservation] = useState<PatrolObservation | null>(null);
  const [isDetailOpen, setIsDetailOpen] = useState(false);
  const { isAdmin } = useUserRoles();
  const { teams: clientTeams } = useClientTeams();
  const { user } = useSupabaseUser();

  const { data: observations, isLoading, refetch } = useQuery({
    queryKey: ['patrol-observations-all', user?.id, isAdmin, clientTeams],
    queryFn: async () => {
      if (!user?.id) return [];

      // For admins, get guards from their admin group
      let guardIdsFilter: string[] | null = null;
      
      if (isAdmin) {
        // Get all members of the admin group (parent + contributors)
        const { data: adminGroupData, error: adminGroupError } = await supabase
          .rpc('get_admin_group', { user_id: user.id });
        
        if (adminGroupError) {
          console.error('Error fetching admin group:', adminGroupError);
          return [];
        }
        
        const adminIds = adminGroupData?.map(ag => ag.admin_id) || [];
        console.log('Admin IDs in group:', adminIds);
        
        if (adminIds.length === 0) {
          console.log('No admin group found');
          return [];
        }
        
        // Get all guards created by these admins
        const { data: guards, error: guardsError } = await supabase
          .from('profiles')
          .select('id')
          .in('created_by', adminIds);
        
        if (guardsError) {
          console.error('Error fetching guards:', guardsError);
          return [];
        }
        
        console.log('Guards created by admin group:', guards?.length);
        
        if (guards && guards.length > 0) {
          guardIdsFilter = guards.map(g => g.id);
        } else {
          console.log('No guards found for admin group');
          return [];
        }
      }

      let query = supabase
        .from('patrol_observations')
        .select('*')
        .order('timestamp', { ascending: false });

      // If not admin, filter by team access
      if (!isAdmin) {
        // Get user's teams (as team member or client)
        const userTeamIds = [...clientTeams];
        
        // Also get teams where user is a team member
        const { data: teamMembers } = await supabase
          .from('team_members')
          .select('team_id')
          .eq('profile_id', user.id);
          
        if (teamMembers) {
          userTeamIds.push(...teamMembers.map(tm => tm.team_id));
        }
        
        // Get teams where user is a manager
        const { data: managerTeams } = await supabase
          .from('manager_teams')
          .select('team_id')
          .eq('manager_id', user.id);
          
        if (managerTeams) {
          userTeamIds.push(...managerTeams.map(mt => mt.team_id));
        }
        
        // Remove duplicates
        const uniqueTeamIds = [...new Set(userTeamIds)];
        
        if (uniqueTeamIds.length > 0) {
          query = query.in('team_id', uniqueTeamIds);
        } else {
          // If user has no team access, return empty array
          return [];
        }
      }

      // Apply admin group filter for admins
      if (isAdmin && guardIdsFilter) {
        query = query.in('guard_id', guardIdsFilter);
      }

      query = query.limit(50);
      const { data, error } = await query;

      if (error) throw error;
      
      // Get guard details separately
      if (data && data.length > 0) {
        const guardIds = [...new Set(data.map(obs => obs.guard_id).filter(Boolean))];
        if (guardIds.length > 0) {
          const { data: profiles } = await supabase
            .from('profiles')
            .select('id, full_name, first_name, last_name')
            .in('id', guardIds);
          
          // Merge guard data
          return data.map(obs => ({
            ...obs,
            profiles: profiles?.find(p => p.id === obs.guard_id) || null
          })) as PatrolObservation[];
        }
      }
      
      return data as PatrolObservation[];
    },
    enabled: !!user?.id
  });

  const handleObservationClick = (observation: PatrolObservation) => {
    setSelectedObservation(observation);
    setIsDetailOpen(true);
  };

  const handleUpdate = () => {
    refetch();
  };

  const handleDelete = async (observationId: string, e: React.MouseEvent) => {
    e.stopPropagation();
    
    if (!window.confirm('Are you sure you want to delete this patrol observation?')) {
      return;
    }

    try {
      const { error } = await supabase
        .from('patrol_observations')
        .delete()
        .eq('id', observationId);

      if (error) throw error;
      
      toast.success('Patrol observation deleted successfully');
      refetch();
    } catch (error) {
      console.error('Error deleting patrol observation:', error);
      toast.error('Failed to delete patrol observation');
    }
  };

  const getSeverityColor = (severity: string) => {
    switch (severity) {
      case 'high':
        return 'bg-red-50 text-red-600 border-red-100';
      case 'medium':
        return 'bg-amber-50 text-amber-600 border-amber-100';
      case 'low':
        return 'bg-emerald-50 text-emerald-600 border-emerald-100';
      default:
        return 'bg-slate-50 text-slate-600 border-slate-100';
    }
  };

  if (isLoading) {
    return (
      <div className="text-center py-12">
        <div className="flex flex-col items-center space-y-4">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500"></div>
          <p className="text-lg font-medium text-muted-foreground">Loading patrol observations...</p>
        </div>
      </div>
    );
  }

  if (!observations?.length) {
    return (
      <Card className="bg-gradient-to-br from-muted/30 to-muted/50 border-blue-200/50">
        <CardContent className="text-center py-16">
          <div className="flex flex-col items-center space-y-6">
            <div className="p-6 bg-gradient-to-br from-blue-100 to-blue-200 rounded-2xl">
              <Eye className="h-16 w-16 text-blue-500" />
            </div>
            <div className="space-y-2">
              <h3 className="text-xl font-semibold text-muted-foreground">No Patrol Observations</h3>
              <p className="text-muted-foreground/70">No patrol observations have been recorded yet.</p>
            </div>
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <>
      <div className="grid gap-6 sm:grid-cols-1 md:grid-cols-2 lg:grid-cols-3">
        {observations.map((observation, index) => (
          <Card 
            key={observation.id} 
            className={cn(
              "group cursor-pointer transform transition-all duration-300 hover:scale-[1.02] hover:shadow-luxury",
              "bg-gradient-to-br from-background/95 via-background/90 to-blue-50/50 border-blue-200/50",
              "hover:from-background hover:to-background hover:border-blue-300/70 dark:hover:from-card dark:hover:to-card dark:hover:border-blue-400/50",
              "backdrop-blur-sm"
            )}
            onClick={() => handleObservationClick(observation)}
            style={{ animationDelay: `${index * 100}ms` }}
          >
            <div className="relative overflow-hidden">
              {/* Gradient overlay */}
              <div className="absolute inset-0 bg-gradient-to-br from-blue-500/5 to-indigo-500/5 group-hover:from-blue-500/15 group-hover:to-indigo-500/15 dark:group-hover:from-blue-500/20 dark:group-hover:to-indigo-500/20 transition-all duration-300"></div>
              
              {/* Shimmer effect */}
              <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/10 to-transparent -skew-x-12 translate-x-[-200%] group-hover:translate-x-[200%] transition-transform duration-1000"></div>
              
              <div className="relative p-6 space-y-4">
                {/* Header */}
                <div className="flex items-start justify-between">
                  <div className="flex items-center gap-3">
                    <div className={cn(
                      "p-2 rounded-xl transition-all duration-300 group-hover:scale-110",
                      observation.severity === 'high' ? 'bg-red-500 text-white' :
                      observation.severity === 'medium' ? 'bg-amber-500 text-white' :
                      'bg-emerald-500 text-white'
                    )}>
                      {observation.severity === 'high' && <AlertTriangle className="h-4 w-4" />}
                      {observation.severity === 'medium' && <Eye className="h-4 w-4" />}
                      {observation.severity === 'low' && <Binoculars className="h-4 w-4" />}
                    </div>
                    <div>
                      <h3 className="font-bold text-lg text-foreground group-hover:text-blue-700 dark:group-hover:text-blue-300 transition-colors line-clamp-1">
                        {observation.title}
                      </h3>
                    </div>
                  </div>
                  
                  <Badge className={`${getSeverityColor(observation.severity)} text-xs border font-medium`}>
                    {observation.severity}
                  </Badge>
                </div>
                
                {/* Description */}
                <p className="text-sm text-muted-foreground leading-relaxed line-clamp-2 group-hover:text-foreground/90 dark:group-hover:text-muted-foreground transition-colors">
                  {observation.description || 'No description available'}
                </p>

                {/* Info Grid */}
                <div className="grid grid-cols-2 gap-4 pt-2">
                  <div className="flex items-center gap-2 p-2 bg-muted/30 rounded-lg">
                    {observation.status === 'resolved' ? (
                      <CheckCircle className="h-3 w-3 text-emerald-500" />
                    ) : (
                      <AlertTriangle className="h-3 w-3 text-amber-500" />
                    )}
                    <span className="text-xs font-medium">{observation.status}</span>
                  </div>
                  <div className="flex items-center gap-2 p-2 bg-muted/30 rounded-lg">
                    <Clock className="h-3 w-3 text-primary" />
                    <span className="text-xs font-medium">{format(new Date(observation.timestamp), 'MMM d, HH:mm')}</span>
                  </div>
                </div>
                
                {/* Footer */}
                <div className="flex items-center justify-between pt-3 border-t border-blue-200/50">
                  <div className="flex items-center gap-2">
                    <User className="h-3 w-3 text-muted-foreground" />
                    <span className="text-xs font-medium text-muted-foreground">
                      {observation.guard_name || 
                       (observation as any).profiles?.full_name || 
                       `${(observation as any).profiles?.first_name || ''} ${(observation as any).profiles?.last_name || ''}`.trim() || 
                       'Unknown Guard'}
                    </span>
                  </div>
                  
                  <div className="flex items-center gap-1">
                    {observation.status === 'resolved' ? (
                      <CheckCircle className="h-4 w-4 text-emerald-500" />
                    ) : (
                      <AlertTriangle className="h-4 w-4 text-amber-500" />
                    )}
                  </div>
                </div>
                
                {/* Admin delete button */}
                {isAdmin && (
                  <div className="absolute top-4 right-4 opacity-0 group-hover:opacity-100 transition-all duration-300">
                    <Button
                      size="sm"
                      variant="destructive"
                      onClick={(e) => handleDelete(observation.id, e)}
                      className="h-8 w-8 p-0 bg-red-500 hover:bg-red-600"
                    >
                      <Trash2 className="h-3 w-3" />
                    </Button>
                  </div>
                )}
                
                {/* Floating action indicator */}
                <div className="absolute bottom-4 right-4 opacity-0 group-hover:opacity-100 transition-all duration-300 group-hover:translate-x-1">
                  <div className="w-8 h-8 border-2 border-blue-500 rounded-full flex items-center justify-center bg-blue-500/10 backdrop-blur-sm">
                    <div className="w-3 h-3 border-t-2 border-r-2 border-blue-500 rotate-45"></div>
                  </div>
                </div>
              </div>
            </div>
          </Card>
        ))}
      </div>

      <AlertDetailDialog
        isOpen={isDetailOpen}
        onClose={() => setIsDetailOpen(false)}
        alert={selectedObservation}
        type="observation"
        onUpdate={handleUpdate}
      />
    </>
  );
};
